import { useCallback, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { usePipelinePresence } from '@/hooks/use-pipeline-presence';
import { usePipelineContext } from '../../context';
import { PipelineCard } from '../PipelineCard';
import { buildCardPresenceMap, makeRegisterCardRef } from '../../utils';
import { useStageFocusRecovery } from '../../hooks';
import type { PipelineStage as PipelineStageType, Customer, PresenceUser } from '../../types';

// Stable empty array for cards with no presence — frozen to prevent accidental mutation
const EMPTY_PRESENCE = Object.freeze([] as PresenceUser[]) as PresenceUser[];

interface OutcomesRowProps {
  stages: PipelineStageType[];
}

/**
 * Collapsible outcomes row for Lost, Not Qualified, and Grant stages
 * Shows summary badges when collapsed, full mini-stages when expanded
 */
export function OutcomesRow({ stages }: OutcomesRowProps) {
  const { toast } = useToast();
  const { state, actions, meta } = usePipelineContext();
  const { presence: pipelinePresence, currentUserId } = usePipelinePresence();
  const { outcomesCollapsed, draggedCustomer } = state;

  // Get customer counts for each stage (call once, derive length)
  const stageCounts = stages.map((stage) => {
    const customers = meta.getCustomersByStage(stage.id);
    return { stage, customers, count: customers.length };
  });

  const totalCount = stageCounts.reduce((sum, s) => sum + s.count, 0);

  // Handle drop on mini-stage
  const handleDrop = useCallback(
    (stage: PipelineStageType, e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.classList.remove('ring-2', 'ring-blue-400');

      if (draggedCustomer && draggedCustomer.pipelineStageId !== stage.id) {
        actions.updateCustomer(draggedCustomer.id, {
          pipelineStageId: stage.id,
          pipelineNotes: draggedCustomer.pipelineNotes || null,
        });

        toast({
          title: 'Customer Moved',
          description: `${draggedCustomer.name} moved to ${stage.name}`,
        });
      }

      actions.onDragEnd();
    },
    [draggedCustomer, actions, toast]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.add('ring-2', 'ring-blue-400');
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.remove('ring-2', 'ring-blue-400');
  }, []);

  // Auto-expand when dragging over collapsed outcomes row
  const handleHeaderDragOver = useCallback(
    (e: React.DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (outcomesCollapsed && draggedCustomer) {
        actions.toggleOutcomesCollapsed();
      }
    },
    [outcomesCollapsed, draggedCustomer, actions]
  );

  // Pre-build per-card presence map (stable references for React.memo)
  const cardPresenceMap = useMemo(
    () => buildCardPresenceMap(pipelinePresence.cards, currentUserId),
    [pipelinePresence.cards, currentUserId]
  );

  // Stable callback for registering card refs
  const registerRef = useMemo(
    () => makeRegisterCardRef(meta.refs.customerCardRefs),
    [meta.refs]
  );

  if (stages.length === 0) return null;

  return (
    <div className="outcomes-row-sticky mt-4 pt-4">
      {/* Collapse/Expand Header - auto-expands when dragging over */}
      <Button
        variant="ghost"
        className={`outcomes-header-button w-full flex items-center justify-between px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg ${
          outcomesCollapsed && draggedCustomer ? 'outcomes-drag-indicator' : ''
        }`}
        onClick={actions.toggleOutcomesCollapsed}
        onDragOver={handleHeaderDragOver}
        onDragEnter={handleHeaderDragOver}
      >
        <div className="flex items-center gap-2">
          {outcomesCollapsed ? (
            <ChevronRight className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
            Other Outcomes
          </span>
          <Badge
            variant="secondary"
            className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
          >
            {totalCount} total
          </Badge>
        </div>

        {/* Summary badges when collapsed */}
        {outcomesCollapsed && (
          <div className="flex items-center gap-2">
            {stageCounts.map(({ stage, count }) => (
              <Badge
                key={stage.id}
                variant="outline"
                className="text-xs"
                style={{
                  borderColor: `${stage.color}50`,
                  color: stage.color,
                }}
              >
                {stage.name}: {count}
              </Badge>
            ))}
          </div>
        )}
      </Button>

      {/* Expanded Content */}
      {!outcomesCollapsed && (
        <div className="mt-3 flex gap-3 overflow-x-auto pb-2">
          {stageCounts.map(({ stage, customers }) => (
            <MiniStage
              key={stage.id}
              stage={stage}
              customers={customers}
              onDrop={(e) => handleDrop(stage, e)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              // Card props from context (threaded to PipelineCard)
              selectedCustomerId={state.selectedCustomerId}
              highlightedCustomerId={state.highlightedCustomerId}
              matchingCustomerIds={meta.matchingCustomerIds}
              searchActive={meta.searchActive}
              focusMatchesOnly={state.focusMatchesOnly}
              isFiltering={state.isFiltering}
              highlightTerms={meta.highlightTerms}
              isCopied={meta.isCopied}
              copyToClipboard={meta.copyToClipboard}
              openCustomerDetail={actions.openCustomerDetail}
              onDragStart={actions.onDragStart}
              onDragEnd={actions.onDragEnd}
              updateLeadScore={actions.updateLeadScore}
              registerRef={registerRef}
              cardPresenceMap={cardPresenceMap}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface MiniStageProps {
  stage: PipelineStageType;
  customers: Customer[];
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  // Card props
  selectedCustomerId: number | null;
  highlightedCustomerId: number | null;
  matchingCustomerIds: Set<number>;
  searchActive: boolean;
  focusMatchesOnly: boolean;
  isFiltering: boolean;
  highlightTerms: string[];
  isCopied: Record<string, boolean>;
  copyToClipboard: (text: string | null | undefined, id: string) => void;
  openCustomerDetail: (customer: Customer) => void;
  onDragStart: (customer: Customer) => void;
  onDragEnd: () => void;
  updateLeadScore: (customerId: number, score: number) => void;
  registerRef: (customerId: number, node: HTMLDivElement | null) => void;
  cardPresenceMap: Map<number, PresenceUser[]>;
}

/**
 * Mini version of a stage for the outcomes row
 * Supports drag-drop but with compact styling
 */
function MiniStage({
  stage,
  customers,
  onDrop,
  onDragOver,
  onDragLeave,
  selectedCustomerId,
  highlightedCustomerId,
  matchingCustomerIds,
  searchActive,
  focusMatchesOnly,
  isFiltering,
  highlightTerms,
  isCopied,
  copyToClipboard,
  openCustomerDetail,
  onDragStart,
  onDragEnd,
  updateLeadScore,
  registerRef,
  cardPresenceMap,
}: MiniStageProps) {
  const miniStageContainerId = `outcomes-stage-panel-${stage.id}`;
  useStageFocusRecovery(customers, miniStageContainerId);

  return (
    <div
      className="flex-shrink-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm overflow-hidden transition-all"
      style={{ width: 'var(--pipeline-stage-width-expanded, 320px)' }}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      {/* Header */}
      <div
        className="px-3 py-2 border-b"
        style={{
          backgroundColor: `${stage.color}10`,
          borderTopColor: stage.color,
          borderTopWidth: '3px',
          borderBottomColor: `${stage.color}30`,
        }}
      >
        <div className="flex items-center justify-between">
          <span
            className="text-sm font-medium"
            style={{ color: stage.color }}
          >
            {stage.name}
          </span>
          <Badge
            variant="secondary"
            className="text-xs"
            style={{
              backgroundColor: `${stage.color}20`,
              color: stage.color,
            }}
          >
            {customers.length}
          </Badge>
        </div>
      </div>

      {/* Cards container */}
      <div
        id={miniStageContainerId}
        tabIndex={-1}
        aria-label={`${stage.name} deals list`}
        className="p-2 max-h-[200px] overflow-y-auto space-y-2 focus-visible:ring-2 focus-visible:ring-emerald-400/60 focus-visible:ring-inset"
        style={{
          scrollbarWidth: 'thin',
          overflowAnchor: 'auto',
        }}
      >
        {customers.length === 0 ? (
          <div className="h-16 border border-dashed border-slate-200 dark:border-slate-700 rounded-md flex items-center justify-center text-xs text-muted-foreground">
            No customers
          </div>
        ) : (
          customers.map((customer) => {
            const isSearchMatch = matchingCustomerIds.has(customer.id);

            return (
              <PipelineCard
                key={customer.id}
                customer={customer}
                stage={stage}
                isSelected={selectedCustomerId === customer.id}
                isHighlighted={highlightedCustomerId === customer.id}
                isSearchMatch={isSearchMatch}
                searchActive={searchActive}
                dimCard={searchActive && !focusMatchesOnly && !isSearchMatch}
                isFiltering={isFiltering}
                highlightTerms={highlightTerms}
                isCopied={isCopied}
                cardPresence={cardPresenceMap.get(customer.id) ?? EMPTY_PRESENCE}
                copyToClipboard={copyToClipboard}
                onOpenDetail={openCustomerDetail}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onUpdateLeadScore={updateLeadScore}
                registerRef={registerRef}
              />
            );
          })
        )}
      </div>
    </div>
  );
}
